import * as React from 'react';
import type { ValidationIssue } from '../../../../types';

interface ClearedLinesApi {
  /** Line indices the user has started editing this cycle. */
  clearedLines: ReadonlySet<number>;
  /** Mark a whole line as locally cleared so all its stale issues are
   * suppressed until the parent revalidates. */
  markLineCleared: (lineIndex: number) => void;
}

/**
 * Locally suppresses a line's validation issues once the user starts editing
 * any field on that line. The existing messages are stale until the parent
 * revalidates, and keeping them on screen while the user types is noisy. The
 * set is reset whenever the parent passes a new `errors` reference (i.e. a new
 * validation cycle).
 */
export function useClearedFields(errors: ValidationIssue[]): ClearedLinesApi {
  const [clearedLines, setClearedLines] = React.useState<ReadonlySet<number>>(() => new Set());

  // Reset on every new validation cycle (new `errors` reference).
  React.useEffect(() => {
    setClearedLines(prev => (prev.size > 0 ? new Set() : prev));
  }, [errors]);
  const markLineCleared = React.useCallback((lineIndex: number) => {
    setClearedLines(prev => {
      if (prev.has(lineIndex)) return prev;
      const next = new Set(prev);
      next.add(lineIndex);
      return next;
    });
  }, []);

  return { clearedLines, markLineCleared };
}
